import React, { useEffect, useState } from 'react';
import { MdSupervisedUserCircle } from 'react-icons/md';
import { Formik, useFormikContext } from 'formik';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import OrganizersSelectList from './OrganizersSelectList';
import { GroupDto } from 'legacy/groups/types';
import { UserLean } from 'legacy/user/types';
import UsersSearch from 'legacy/common/components/wysiwyg-form/UsersSearch';
import OrganizationsSearch from 'legacy/common/components/wysiwyg-form/OrganizationsSearch';
import Checkbox from 'legacy/common/components/wysiwyg-form/Checkbox';
import OrganizationsList from 'legacy/common/components/wysiwyg-form/OrganizationsList';
import UsersList from 'legacy/common/components/wysiwyg-form/UsersList';
import { shouldEnableOrganizationFee } from '../../../helpers';
import { useTranslation } from 'react-i18next';

type FormValues = {
  organizations: GroupDto[];
  operators: UserLean[];
  scanners: UserLean[];
  photographers: UserLean[];
  cohosts: UserLean[];
};

function OrganizersHostModal({ onClose, isOpen }) {
  const { t } = useTranslation(['events']);
  const {
    values: {
      groups = [],
      cohosts = [],
      scanners = [],
      photographers = [],
      operators = [],
      ...otherFormValues
    },
    setValues
  } = useFormikContext<any>();
  const [isHostedByOrganization, setIsHostedByOrganization] = useState(
    groups?.length > 0
  );

  const toggleHostedByOrganization = () => {
    setIsHostedByOrganization((prevState) => !prevState);
  };

  const onCloseHandler = () => {
    setIsHostedByOrganization(false);
    onClose();
  };

  const onSave = (newValues) => {
    let groupsValue = [];

    // If user unchecked the hosted by organization checkbox, empty the groups.
    if (isHostedByOrganization) {
      groupsValue = newValues.organizations;
    }

    setValues({
      ...otherFormValues,
      cohosts: newValues.cohosts,
      groups: groupsValue,
      operators: newValues.operators,
      scanners: newValues.scanners,
      photographers: newValues.photographers,
      chargeOrganizationFee: shouldEnableOrganizationFee(
        newValues.organizations
      )
    });

    onCloseHandler();
  };

  useEffect(() => {
    // If user is opening the modal AND we have groups, set the event has being hosted by an organization.
    // We need this because this component can be mounted before we get the data from the API, which means the default value from the useState will not be right.
    if (isOpen && groups?.length > 0 && !isHostedByOrganization) {
      setIsHostedByOrganization(true);
    }
  }, [groups, isOpen]);

  return (
    <Formik<FormValues>
      initialValues={{
        organizations: groups,
        cohosts,
        operators,
        scanners,
        photographers
      }}
      enableReinitialize
      onSubmit={onSave}
    >
      {({ submitForm, resetForm, values: innerValues }) => {
        const associationId = innerValues.organizations?.find(
          (g) => g.associationDependentId
        )?.associationDependentId;

        return (
          <Modal
            visible={isOpen}
            onClose={() => {
              resetForm();
              onCloseHandler();
            }}
            type={ModalType.SMALL}
            title={t('events:eventDetails.organizers')}
            icon={<MdSupervisedUserCircle size={20} />}
            renderFooter={() => (
              <Button type={ButtonType.Primary} onClick={submitForm}>
                {t('events:host.saveOrganizers')}
              </Button>
            )}
          >
            <div className="pt-7 pb-[70px] px-4">
              <p className="font-normal text-base text-gray-500 pb-7">
                {t('events:host.manageEventOrganizers')}
              </p>

              <Checkbox
                id="is-hosted-by-org"
                label={t('events:host.isHostedByOrganization')}
                checked={isHostedByOrganization}
                onChange={toggleHostedByOrganization}
              />
              {isHostedByOrganization && (
                <OrganizersSelectList
                  field="organizations"
                  label={t('events:host.organizations')}
                  placeholder={t('events:host.selectOrganizations')}
                  SearchComponent={OrganizationsSearch}
                  ListComponent={OrganizationsList}
                />
              )}
              <OrganizersSelectList
                field="cohosts"
                label={t('events:host.cohosts')}
                placeholder={t('events:host.selectCohosts')}
                SearchComponent={UsersSearch}
                ListComponent={UsersList}
                associationId={associationId}
              />
              <OrganizersSelectList
                field="operators"
                label={t('events:host.operators')}
                placeholder={t('events:host.selectOperators')}
                SearchComponent={UsersSearch}
                ListComponent={UsersList}
                associationId={associationId}
              />
              <OrganizersSelectList
                field="scanners"
                label={t('events:host.scanners')}
                placeholder={t('events:host.selectScanners')}
                SearchComponent={UsersSearch}
                ListComponent={UsersList}
                associationId={associationId}
              />
              <OrganizersSelectList
                field="photographers"
                label={t('events:host.photographers')}
                placeholder={t('events:host.selectPhotographers')}
                SearchComponent={UsersSearch}
                ListComponent={UsersList}
                menuPlacement="top"
                associationId={associationId}
              />
            </div>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default OrganizersHostModal;
